import { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useInspection, useUpdateInspection, InspectionData } from "@/hooks/use-inspections";
import { ChevronLeft, Save, CheckCircle, Loader2, AlertCircle, MapPin, Phone, Mail as MailIcon, User as UserIcon, X, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SignaturePad from "@/components/signature-pad";
import { useActiveSites, Site } from "@/hooks/use-sites";
import { BRANDS } from "@/lib/constants";

import { PpmPhotosProvider, usePpmPhotosContext } from "@/hooks/use-ppm-photos-context";
import { useListPpmInspectionPhotos, getListPpmInspectionPhotosQueryKey } from "@workspace/api-client-react";
import { PpmTaskRow } from "@/components/ppm-task-row";

export default function EngineerInspectionForm() {
  return (
    <PpmPhotosProvider>
      <EngineerInspectionFormContent />
    </PpmPhotosProvider>
  );
}

function EngineerInspectionFormContent() {
  const { data: user, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  const params = useParams();
  const id = parseInt(params.id || "0", 10);
  const { data: inspection, isLoading: isInspLoading } = useInspection(id);
  const { data: photos = [], isLoading: isPhotosLoading, isError: isPhotosError, refetch: refetchPhotos } = useListPpmInspectionPhotos(id, { query: { enabled: id > 0, queryKey: getListPpmInspectionPhotosQueryKey(id) } });
  const { data: activeSites, isLoading: isSitesLoading } = useActiveSites();
  const updateInspection = useUpdateInspection();
  const { toast } = useToast();
  const { pendingOperations } = usePpmPhotosContext();

  const initializedRef = useRef(false);
  const [formData, setFormData] = useState<InspectionData | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [selectorBrand, setSelectorBrand] = useState<string>("");

  const isReadOnly = !user || (inspection?.status === "completed" && user?.role === "engineer");

  useEffect(() => {
    if (!isAuthLoading && !user) setLocation("/admin");
  }, [user, isAuthLoading, setLocation]);

  useEffect(() => {
    if (inspection && !isSitesLoading && activeSites && !initializedRef.current) {
      setFormData(inspection.data);
      
      let initialBrand = "";
      if (inspection.data.siteBrand !== undefined && inspection.data.siteBrand !== null) {
        initialBrand = inspection.data.siteBrand;
      } else if (inspection.data.siteId) {
        const site = activeSites.find(s => s.id === inspection.data.siteId);
        if (site) {
          initialBrand = site.brand || "";
        }
      }
      setSelectorBrand(initialBrand);

      initializedRef.current = true;
    }
  }, [inspection, user, isSitesLoading, activeSites]);

  if (isAuthLoading || isInspLoading || isSitesLoading || isPhotosLoading || !formData) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const handleBrandChange = (newBrand: string) => {
    if (isReadOnly) return;
    setSelectorBrand(newBrand);
    setFormData(prev => prev ? {
      ...prev,
      siteBrand: newBrand,
      siteId: null,
      siteName: "",
      siteAddress: "",
      sitePhone: null,
      siteEmail: null,
      siteContactName: null,
      siteCoverImagePath: null
    } : prev);
  };

  const filteredSites = activeSites?.filter(s => (s.brand || "") === selectorBrand) || [];

  const updateField = (path: keyof InspectionData, value: any) => {
    setFormData(prev => prev ? { ...prev, [path]: value } : prev);
  };

  const updateTask = (key: keyof InspectionData['tasks'], value: boolean) => {
    setFormData(prev => prev ? { ...prev, tasks: { ...prev.tasks, [key]: value } } : prev);
  };

  const validateForComplete = (): string[] => {
    if (!formData) return ["Form data is missing"];
    const errors: string[] = [];

    if (formData.safetyAccess !== "yes") errors.push("Safety access must be yes before completing an inspection");
    
    // Check missing fields (matches backend)
    if (!formData.siteName.trim()) errors.push("site name is required before completing an inspection");
    if (!formData.siteAddress.trim()) errors.push("site address is required before completing an inspection");
    if (!formData.assetId.trim()) errors.push("asset ID is required before completing an inspection");
    if (!formData.equipmentLocation.trim()) errors.push("equipment location is required before completing an inspection");
    if (!formData.refrigerantType.trim()) errors.push("refrigerant type is required before completing an inspection");
    if (!formData.holdingTemp.trim()) errors.push("holding temperature is required before completing an inspection");
    if (!formData.inspectionDate) errors.push("inspection date is required before completing an inspection");
    if (!formData.engineerSignature.trim()) errors.push("engineer signature is required before completing an inspection");
    if (!formData.clientSignature.trim()) errors.push("client signature is required before completing an inspection");

    // All selections required (mentioned in prompt: "all selections")
    if (!["six_monthly", "annual"].includes(formData.maintenanceInterval)) errors.push("Maintenance Interval must be selected.");
    if (!["yes", "not_required"].includes(formData.isolation)) errors.push("Isolation Verification must be selected.");
    if (!["yes", "digital_checked", "no"].includes(formData.fgasLogbook)) errors.push("F-Gas Logbook status must be selected.");
    if (!["none", "repaired", "isolated"].includes(formData.leakStatus)) errors.push("Refrigerant Leak Status must be selected.");
    if (!["yes", "no"].includes(formData.systemOperational)) errors.push("Final System Status must be selected.");

    const coreTasks = ["condenserCoil", "evaporatorCoil", "condensateDrains", "leakDetection", "doorSeals", "operationalPressures"] as const;
    for (const t of coreTasks) {
      if (!formData.tasks[t]) errors.push(`Task ${t} must be completed`);
    }

    if (formData.maintenanceInterval === "annual") {
      const annualTasks = ["electricalTerminals", "insulationResistance", "safetyCutouts", "alarmsReliefValves", "compressorOil", "defrostElements"] as const;
      for (const t of annualTasks) {
        if (!formData.tasks[t]) errors.push(`Task ${t} must be completed`);
      }
    }
    
    if (formData.systemOperational === "no" && !formData.notes.trim()) {
      errors.push("Notes are required when the system is not operational");
    }

    return errors;
  };

  const handleSave = (status: "draft" | "completed") => {
    if (!formData) return;
    
    if (status === "completed") {
      const errs = validateForComplete();
      if (errs.length > 0) {
        setValidationErrors(errs);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
    }
    
    setValidationErrors([]);
    updateInspection.mutate({ id, payload: { status, data: formData } }, {
      onSuccess: () => {
        toast({ title: status === "completed" ? "Inspection completed" : "Draft saved successfully" });
        if (status === "completed" && user?.role === "engineer") {
          setLocation("/engineer/ppm");
        }
      },
      onError: (err: any) => {
        toast({ title: "Save failed", description: err.message, variant: "destructive" });
      }
    });
  };

  const backUrl = user?.role === "admin" ? "/admin/engineer-ppm" : "/engineer/ppm";

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col pb-24">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm print:hidden">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setLocation(backUrl)} className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex flex-col">
              <span className="font-bold text-slate-900 leading-tight">
                {inspection?.status === "completed" ? "Completed PPM" : "Edit Draft"}
              </span>
              <span className="text-[10px] text-slate-500 font-medium">#{id}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {inspection?.status === "completed" && (
              <button onClick={() => window.print()} className="px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
                Print
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto p-4 md:p-6 space-y-6 print:p-0 print:m-0 print:max-w-none">
        {/* Print Header */}
        <div className="hidden print:block text-center mb-8 border-b-2 border-slate-900 pb-4">
          <h1 className="text-2xl font-bold text-slate-900">REFRIGERATION PPM INSPECTION FORM</h1>
          <p className="text-sm font-bold text-slate-600">Planned Preventative Maintenance Checklist</p>
        </div>

        {validationErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 print:hidden">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 text-sm">Cannot complete inspection</h3>
                <ul className="mt-2 space-y-1 text-sm text-red-700 list-disc list-inside">
                  {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}

        {isPhotosError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 print:hidden">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-red-800 text-sm">Failed to load photos</h3>
                  <p className="mt-1 text-sm text-red-700">Attachments could not be loaded. Please retry before uploading new ones.</p>
                </div>
                <button 
                  onClick={() => refetchPhotos()}
                  className="px-3 py-1.5 text-xs font-medium bg-red-100 hover:bg-red-200 text-red-800 rounded-lg transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}

        <Section title="SECTION 1: PRE-WORK SCREENING & SAFETY">
          <p className="text-xs text-slate-500 italic mb-4">The engineer must complete this section before commencing any maintenance work.</p>
          
          <div className="space-y-5">
            <div>
              <p className="text-sm font-medium text-slate-900 mb-2">1. Safety & Access: Is the work area safe, clear of hazards, and properly ventilated?</p>
              <div className="flex flex-wrap gap-4">
                <Radio label="Yes" name="safetyAccess" value="yes" current={formData.safetyAccess} onChange={v => updateField("safetyAccess", v)} disabled={isReadOnly} />
                <Radio label="No (Stop work and resolve hazard)" name="safetyAccess" value="no" current={formData.safetyAccess} onChange={v => updateField("safetyAccess", v)} disabled={isReadOnly} />
              </div>
            </div>
            
            <div className="h-px bg-slate-100" />
            
            <div>
              <p className="text-sm font-medium text-slate-900 mb-2">2. Isolation Verification: Has the equipment been safely isolated electrically and tagged out?</p>
              <div className="flex flex-wrap gap-4">
                <Radio label="Yes" name="isolation" value="yes" current={formData.isolation} onChange={v => updateField("isolation", v)} disabled={isReadOnly} />
                <Radio label="Not required for this task" name="isolation" value="not_required" current={formData.isolation} onChange={v => updateField("isolation", v)} disabled={isReadOnly} />
              </div>
            </div>

            <div className="h-px bg-slate-100" />

            <div>
              <p className="text-sm font-medium text-slate-900 mb-2">3. Pre-Service Log: Are there any active alarms on the controller, or has the client reported performance issues?</p>
              <textarea
                value={formData.preServiceNotes}
                onChange={e => updateField("preServiceNotes", e.target.value)}
                disabled={isReadOnly}
                placeholder="Enter notes..."
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-none"
              />
            </div>

            <div className="h-px bg-slate-100" />

            <div>
              <p className="text-sm font-medium text-slate-900 mb-2">4. F-Gas Logbook: Is the statutory F-Gas logbook available and up to date?</p>
              <div className="flex flex-wrap gap-4">
                <Radio label="Yes" name="fgasLogbook" value="yes" current={formData.fgasLogbook} onChange={v => updateField("fgasLogbook", v)} disabled={isReadOnly} />
                <Radio label="No" name="fgasLogbook" value="no" current={formData.fgasLogbook} onChange={v => updateField("fgasLogbook", v)} disabled={isReadOnly} />
                <Radio label="Digital Record Checked" name="fgasLogbook" value="digital_checked" current={formData.fgasLogbook} onChange={v => updateField("fgasLogbook", v)} disabled={isReadOnly} />
              </div>
            </div>
          </div>
        </Section>

        <Section title="SECTION 2: ASSET IDENTIFICATION">
          <div className="mb-6 space-y-4">
            <h3 className="text-sm font-medium text-slate-900 hidden print:block">Site Information</h3>
            
            <div className="print:hidden mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Site Brand</label>
                <select 
                  value={selectorBrand}
                  onChange={e => handleBrandChange(e.target.value)}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-slate-50 disabled:opacity-80"
                >
                  <option value="">No Brand</option>
                  {BRANDS.map(b => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                  {selectorBrand && !BRANDS.includes(selectorBrand as any) && (
                    <option value={selectorBrand}>{selectorBrand} (Archived)</option>
                  )}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Select a Registered Site (Optional)</label>
                <select 
                  value={formData.siteId || ""} 
                  onChange={e => {
                    const id = e.target.value ? parseInt(e.target.value, 10) : null;
                    if (!id) {
                      setFormData(prev => prev ? {
                        ...prev,
                        siteId: null,
                        siteName: "",
                        siteAddress: "",
                        sitePhone: null,
                        siteEmail: null,
                        siteContactName: null,
                        siteCoverImagePath: null
                      } : prev);
                    } else {
                      const site = activeSites?.find(s => s.id === id);
                      if (site) {
                        setFormData(prev => prev ? {
                          ...prev,
                          siteId: site.id,
                          siteBrand: site.brand || "",
                          siteName: site.name,
                          siteAddress: site.address,
                          sitePhone: site.phone,
                          siteEmail: site.email,
                          siteContactName: site.contactName,
                          siteCoverImagePath: site.coverImagePath,
                        } : prev);
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm bg-white disabled:bg-slate-50 disabled:opacity-80"
                >
                  <option value="">-- Legacy Free-Text / No Linked Site --</option>
                  {filteredSites.map(site => (
                    <option key={site.id} value={site.id}>{site.name} — {site.address}</option>
                  ))}
                  {formData.siteId && !filteredSites.find(s => s.id === formData.siteId) && (
                    <option value={formData.siteId} disabled>
                      {formData.siteName} (Archived / Unavailable)
                    </option>
                  )}
                </select>
              </div>
            </div>

            {formData.siteId ? (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col sm:flex-row print:border-none print:shadow-none">
                {formData.siteCoverImagePath && (
                  <div className="w-full sm:w-1/3 h-40 sm:h-auto bg-slate-100 relative shrink-0 print:w-32 print:h-32">
                    <img src={`${import.meta.env.BASE_URL}${formData.siteCoverImagePath.replace(/^\//, "")}`} alt="Site cover" className="w-full h-full object-cover absolute inset-0" />
                  </div>
                )}
                <div className="p-4 flex-1 print:px-0">
                  <h4 className="font-bold text-slate-900 text-lg flex items-center flex-wrap gap-2">
                    {formData.siteName || "Unnamed Site"}
                    {formData.siteBrand ? <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded-full font-bold uppercase tracking-wider print:hidden">{formData.siteBrand}</span> : null}
                  </h4>
                  <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap print:text-xs">{formData.siteAddress || "No address provided"}</p>
                  
                  <div className="mt-4 grid grid-cols-1 gap-2 print:mt-2">
                    {formData.siteContactName && (
                      <div className="flex items-center gap-2 text-sm text-slate-700 print:text-xs">
                        <UserIcon className="w-4 h-4 text-slate-400 shrink-0 print:w-3 print:h-3" />
                        <span>{formData.siteContactName}</span>
                      </div>
                    )}
                    {formData.sitePhone && (
                      <div className="flex items-center gap-2 text-sm text-slate-700 print:text-xs">
                        <Phone className="w-4 h-4 text-slate-400 shrink-0 print:w-3 print:h-3" />
                        <a href={`tel:${formData.sitePhone}`} className="hover:text-primary transition-colors text-slate-700 print:text-slate-900">{formData.sitePhone}</a>
                      </div>
                    )}
                    {formData.siteEmail && (
                      <div className="flex items-center gap-2 text-sm text-slate-700 print:text-xs">
                        <MailIcon className="w-4 h-4 text-slate-400 shrink-0 print:w-3 print:h-3" />
                        <a href={`mailto:${formData.siteEmail}`} className="hover:text-primary transition-colors text-slate-700 print:text-slate-900">{formData.siteEmail}</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Site Name" value={formData.siteName} onChange={v => updateField("siteName", v)} disabled={isReadOnly || !!formData.siteId} />
                <Field label="Site Address" value={formData.siteAddress} onChange={v => updateField("siteAddress", v)} disabled={isReadOnly || !!formData.siteId} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
            <Field label="Asset Name / ID Tag" value={formData.assetId} onChange={v => updateField("assetId", v)} disabled={isReadOnly} />
            <Field label="Equipment Location" value={formData.equipmentLocation} onChange={v => updateField("equipmentLocation", v)} disabled={isReadOnly} />
            <Field label="Refrigerant Type" value={formData.refrigerantType} onChange={v => updateField("refrigerantType", v)} disabled={isReadOnly} />
            <Field label="Current Holding Temp (°C)" type="number" value={formData.holdingTemp} onChange={v => updateField("holdingTemp", v)} disabled={isReadOnly} />
            <Field label="Inspection Date" type="date" value={formData.inspectionDate} onChange={v => updateField("inspectionDate", v)} disabled={isReadOnly} />
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-sm font-medium text-slate-900 mb-2">Maintenance Interval</p>
            <div className="flex flex-wrap gap-4">
              <Radio label="6-Monthly (Semi-Annual)" name="maintenanceInterval" value="six_monthly" current={formData.maintenanceInterval} onChange={v => updateField("maintenanceInterval", v)} disabled={isReadOnly} />
              <Radio label="Annual (Yearly)" name="maintenanceInterval" value="annual" current={formData.maintenanceInterval} onChange={v => updateField("maintenanceInterval", v)} disabled={isReadOnly} />
            </div>
          </div>
        </Section>

        <Section title="SECTION 3: MAINTENANCE CHECKLISTS">
          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-sm text-slate-900 mb-1">
                {formData.maintenanceInterval === "annual"
                  ? "GROUP A: CORE TASKS"
                  : "GROUP A: 6-MONTHLY (SEMI-ANNUAL) CORE TASKS"}
              </h3>
              <p className="text-xs text-slate-500 italic mb-3">
                {formData.maintenanceInterval === "annual"
                  ? "Complete all core tasks below."
                  : "Complete for all standard 6-month intervals."}
              </p>
              <div className="space-y-4">
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="condenserCoil" label="Condenser Coil: Cleaned, degreased, and checked for optimal airflow." checked={formData.tasks.condenserCoil} onChange={c => updateTask("condenserCoil", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="evaporatorCoil" label="Evaporator Coil: Cleaned, sanitized, and inspected for ice build-up." checked={formData.tasks.evaporatorCoil} onChange={c => updateTask("evaporatorCoil", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="condensateDrains" label="Condensate Drains: Drip trays cleaned; drain lines flushed and verified free-flowing." checked={formData.tasks.condensateDrains} onChange={c => updateTask("condensateDrains", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="leakDetection" label="Leak Detection: F-Gas leak sweep completed on all major joints and valves." checked={formData.tasks.leakDetection} onChange={c => updateTask("leakDetection", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="doorSeals" label="Door & Structural Seals: Gaskets inspected, magnetic pull verified, and hinges lubricated." checked={formData.tasks.doorSeals} onChange={c => updateTask("doorSeals", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="operationalPressures" label="Operational Pressures: Operating pressures, superheat, and subcooling verified within spec." checked={formData.tasks.operationalPressures} onChange={c => updateTask("operationalPressures", c)} disabled={isReadOnly} />
              </div>
            </div>

            {formData.maintenanceInterval === "annual" && <div className="border-t border-slate-200 pt-6">
              <h3 className="font-bold text-sm text-slate-900 mb-1">GROUP B: ANNUAL (YEARLY) DEEP SERVICING TASKS</h3>
              <p className="text-xs text-slate-500 italic mb-3">Complete ALL Group A tasks PLUS the following items.</p>
              <div className="space-y-4">
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="electricalTerminals" label="Electrical Terminals: Connections tightened; contactors inspected for pitting or wear." checked={formData.tasks.electricalTerminals} onChange={c => updateTask("electricalTerminals", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="insulationResistance" label="Insulation Resistance: Compressor motor insulation tested (Megger test recorded)." checked={formData.tasks.insulationResistance} onChange={c => updateTask("insulationResistance", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="safetyCutouts" label="Safety Cut-outs: High/Low pressure safety switches function-tested and calibrated." checked={formData.tasks.safetyCutouts} onChange={c => updateTask("safetyCutouts", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="alarmsReliefValves" label="Alarms & Relief Valves: 'Man-trapped' panic alarms tested; pressure relief valves (PRVs) inspected." checked={formData.tasks.alarmsReliefValves} onChange={c => updateTask("alarmsReliefValves", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="compressorOil" label="Compressor Oil: Oil levels, color, and acidity verified." checked={formData.tasks.compressorOil} onChange={c => updateTask("compressorOil", c)} disabled={isReadOnly} />
                <PpmTaskRow inspectionId={id} photos={photos} photosDisabled={isReadOnly || isPhotosError} taskKey="defrostElements" label="Defrost Elements: Electrical continuity verified on defrost and drain line heaters." checked={formData.tasks.defrostElements} onChange={c => updateTask("defrostElements", c)} disabled={isReadOnly} />
              </div>
            </div>}
          </div>
        </Section>

        <Section title="SECTION 4: SIGN-OFF & COMPLIANCE">
          <div className="space-y-5">
            <div>
              <p className="text-sm font-medium text-slate-900 mb-2">1. Refrigerant Leak Status:</p>
              <div className="flex flex-col gap-2">
                <Radio label="No leaks found." name="leakStatus" value="none" current={formData.leakStatus} onChange={v => updateField("leakStatus", v)} disabled={isReadOnly} />
                <Radio label="Leak identified and repaired immediately." name="leakStatus" value="repaired" current={formData.leakStatus} onChange={v => updateField("leakStatus", v)} disabled={isReadOnly} />
                <Radio label="Leak identified; asset isolated awaiting parts." name="leakStatus" value="isolated" current={formData.leakStatus} onChange={v => updateField("leakStatus", v)} disabled={isReadOnly} />
              </div>
            </div>

            <div className="h-px bg-slate-100" />

            <div>
              <p className="text-sm font-medium text-slate-900 mb-2">2. Final System Status: Is the equipment operational and achieving setpoint temperatures?</p>
              <div className="flex flex-wrap gap-4 mb-3">
                <Radio label="Yes" name="systemOperational" value="yes" current={formData.systemOperational} onChange={v => updateField("systemOperational", v)} disabled={isReadOnly} />
                <Radio label="No (Detail reasons in notes below)" name="systemOperational" value="no" current={formData.systemOperational} onChange={v => updateField("systemOperational", v)} disabled={isReadOnly} />
              </div>
              <textarea
                value={formData.notes}
                onChange={e => updateField("notes", e.target.value)}
                disabled={isReadOnly}
                placeholder="Additional notes or reasons for non-operational status..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 resize-none"
              />
            </div>

            <div className="h-px bg-slate-100" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SignaturePad label="Engineer signature" value={formData.engineerSignature} onChange={v => updateField("engineerSignature", v)} disabled={isReadOnly} />
              <SignaturePad label="Client signature" value={formData.clientSignature} onChange={v => updateField("clientSignature", v)} disabled={isReadOnly} />
            </div>
            
            {inspection?.status === "completed" && (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-center gap-2 text-emerald-800">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold text-sm">Completed by {inspection.engineerName}</span>
              </div>
            )}
          </div>
        </Section>

      </main>

      {!isReadOnly && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-40 print:hidden">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center gap-3 justify-between">
            <div className="text-xs text-slate-500 font-medium w-full sm:w-auto text-center sm:text-left">
              Photos save automatically. Checklist changes still need Save Draft.
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <button
                onClick={() => handleSave("draft")}
                disabled={updateInspection.isPending || pendingOperations > 0}
                className="px-6 py-2.5 rounded-lg border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> {updateInspection.isPending ? "Saving..." : "Save Draft"}
              </button>
              <button
                onClick={() => handleSave("completed")}
                disabled={updateInspection.isPending || pendingOperations > 0}
                className="px-6 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4" /> {updateInspection.isPending ? "Completing..." : "Complete Form"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string, children: React.ReactNode }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm print:border-none print:shadow-none print:break-inside-avoid">
      <div className="px-5 py-3 bg-slate-900 border-b border-slate-800 print:bg-white print:border-slate-300 print:px-0 print:py-2">
        <h2 className="text-white font-bold text-sm tracking-wide print:text-slate-900 print:text-lg">{title}</h2>
      </div>
      <div className="p-5 print:px-0">
        {children}
      </div>
    </section>
  );
}

function Field({ label, value, onChange, disabled, type = "text" }: { label: string, value: string, onChange: (v: string) => void, disabled?: boolean, type?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm disabled:bg-slate-50 disabled:text-slate-700 print:border-none print:border-b print:rounded-none print:px-0 print:bg-transparent"
      />
    </div>
  );
}

function Radio({ label, name, value, current, onChange, disabled }: { label: string, name: string, value: string, current: string, onChange: (v: string) => void, disabled?: boolean }) {
  const isChecked = current === value;
  return (
    <label className={`flex items-center gap-2 cursor-pointer group ${disabled ? 'opacity-80' : ''}`}>
      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${isChecked ? 'border-primary bg-primary' : 'border-slate-300 bg-white group-hover:border-primary/50'}`}>
        {isChecked && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
      </div>
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {/* Hidden input for accessibility/forms if needed */}
      <input type="radio" name={name} value={value} checked={isChecked} onChange={() => onChange(value)} disabled={disabled} className="sr-only" />
    </label>
  );
}

function Checkbox({ label, checked, onChange, disabled }: { label: string, checked: boolean, onChange: (v: boolean) => void, disabled?: boolean }) {
  return (
    <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${disabled ? 'opacity-80' : 'hover:bg-slate-50'} ${checked ? 'border-primary/30 bg-primary/5' : 'border-slate-200'}`}>
      <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${checked ? 'border-primary bg-primary' : 'border-slate-300 bg-white'}`}>
        {checked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>}
      </div>
      <span className="text-sm font-medium text-slate-700 leading-snug">{label}</span>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} className="sr-only" />
    </label>
  );
}
